import { toDeviceTime } from '@app/utils/clockOffset'
import { createSelector } from '@app/utils/createSelector'

import { callApi } from '@app/store/actions/api'
import { createReduxSlice } from '@app/store/redux_slice'
import { createThunk } from '@app/store/thunk'

export type GuestChatSession = {
  channelId: string
  accessToken: string
  /** Epoch ms on this device's clock, which is what the reuse check and the chat socket compare it with. */
  expiresAt: number
}

const guestChatSessionSlice = createReduxSlice<GuestChatSession>('guest_chat_session')

export const guestChatSessionSelector = createSelector([guestChatSessionSlice.selector], (slice): GuestChatSession | null => slice ?? null)

export const hasGuestChatSessionSelector = createSelector([guestChatSessionSelector], session => session !== null)

/** Idempotent server-side by the httpOnly session cookie, so a second call is the token refresh. */
export function ensureGuestChatSession(options: { refresh?: boolean } = {}) {
  return createThunk(async (dispatch, getState, context): Promise<GuestChatSession> => {
    const current = guestChatSessionSelector(getState())
    if (current && !options.refresh && current.expiresAt > Date.now() + REFRESH_SKEW_MS) return current

    // One request at a time: calls that leave before the first answer carry no key cookie yet,
    // and each would raise a visitor of its own (front#130).
    const root = context.getRootContext()
    const inFlight = root.lookup<Promise<GuestChatSession>>(InFlightSymbol)
    if (inFlight) return inFlight

    const request = dispatch(raiseGuestChatSession()).finally(() => root.unset(InFlightSymbol))
    root.set(InFlightSymbol, request)
    return request
  })
}

const raiseGuestChatSession = () =>
  createThunk(async (dispatch): Promise<GuestChatSession> => {
    const { postSupportGuestSession } = await import('@app/packages/api/v3/actions/support')
    const response = await dispatch(callApi(postSupportGuestSession()))
    const { channel_id, access_token, expires_at } = response.data.attributes

    const session: GuestChatSession = {
      channelId: channel_id,
      accessToken: access_token,
      expiresAt: toDeviceTime(new Date(expires_at).getTime()),
    }
    dispatch(guestChatSessionSlice.set(session))

    return session
  })

const InFlightSymbol = Symbol('guest chat session request')

// Refresh a little before the server-side expiry so an in-flight socket
// handshake does not race the deadline.
const REFRESH_SKEW_MS = 60_000
